import { useAnalysisPDFStyles } from "@hooks/useAnalysisPDFStyle";
import { analysisCommentsKeyMapping } from "@models/platform-analysis/entities/comment";
import { AnalysisStepDTO } from "@models/platform-analysis/entities/step";
import {
  PDFAnalysisComment,
  ProductRecommendationDTO,
  SummaryCommentSettingDto,
} from "@models/platform-analysis/entities/steps/summary";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { Image, Text, View } from "@react-pdf/renderer";
import { PDFRichText } from "../../export-pdf/components/rich-text";
import { RecommendationLabel } from "../../export-pdf/components/tables/recommendation-label";
import { PDFTable } from "../../export-pdf/components/tables/table";
import { PDFTableBody } from "../../export-pdf/components/tables/table-body";
import { PDFTableCell } from "../../export-pdf/components/tables/table-cell";
import { PDFTableRow } from "../../export-pdf/components/tables/table-row";
import { PDFTitle } from "../../export-pdf/components/title";
import {
  PDFDefaultPlatformLogoS3,
  PDFStyleConfigs,
  USE_PDF_LOCAL_ASSETS,
} from "../../export-pdf/configs";
import {
  checkContainsMultipleLinesRecommendationBadge,
  generatePDFImageIconUrl,
  parseCommentProductLogo,
} from "../../export-pdf/utils";
import { PDFReviewComment } from "@pages/reviews/components/buttons/export-pdf/model";

export const PDFPlatformSpecificComments = ({
  comments,
  isExpandTable = false,
  isAppendix = false,
  analysisStep,
  commentSetting,
  productRecommendations,
  isReview,
}: {
  comments?: PDFAnalysisComment[];
  isExpandTable?: boolean;
  isAppendix?: boolean;
  analysisStep?: AnalysisStepDTO;
  commentSetting?: SummaryCommentSettingDto;
  productRecommendations?: Record<string, Partial<ProductRecommendationDTO>>;
  isReview?: boolean;
}) => {
  const { typographyStyle } = useAnalysisPDFStyles();

  const steps = analysisStep?.steps.filter((step) => {
    switch (step) {
      case AnalysisStep.Setup:
        return false;
      case AnalysisStep.Feature:
        return commentSetting?.showFeature;
      case AnalysisStep.BusinessMetric:
        return commentSetting?.showBusinessMetric;
      case AnalysisStep.Fee:
        return commentSetting?.showFee;
      case AnalysisStep.Summary:
        return commentSetting?.showSummary;
      default:
        return true;
    }
  });

  const sizes = {
    firstCellWidth: 0.116 * PDFStyleConfigs.TableWidth,
    secondCellWidth: 0.097 * PDFStyleConfigs.TableWidth,
    cellWidth:
      ((isAppendix
        ? 0.19675 * PDFStyleConfigs.TableWidth
        : 0.221 * PDFStyleConfigs.TableWidth) *
        (analysisStep?.steps?.filter((step) => step !== AnalysisStep.Setup)
          .length ?? 1)) /
      (steps?.length ?? 1),
  };

  return (
    <View>
      <PDFTitle
        title={`${
          isReview
            ? isAppendix
              ? "Platforms that were considered but not selected"
              : "Platform specific comments"
            : "Platforms comments"
        }${isExpandTable ? " (continued)" : ""}`}
      />

      <View style={{ gap: PDFStyleConfigs.GapXSmall }}>
        <PDFTable tableWidth={PDFStyleConfigs.TableWidth}>
          <PDFTableRow
            type={
              isAppendix
                ? "AppendixCommentTableHeaderHeight"
                : "CommentTableHeaderHeight"
            }
            forceShowBorderBottom
          >
            <PDFTableCell width={sizes?.firstCellWidth}>
              <Text
                style={{
                  ...typographyStyle?.body2,
                  fontWeight: "bold",
                }}
              >
                Platform
              </Text>
            </PDFTableCell>
            {isAppendix && (
              <PDFTableCell width={sizes.secondCellWidth}>
                <Text
                  style={{
                    ...typographyStyle?.body2,
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  Features match
                </Text>
              </PDFTableCell>
            )}

            {steps?.map((step) => (
              <PDFTableCell key={step} width={sizes?.cellWidth}>
                <Text style={{ ...typographyStyle?.body2, fontWeight: "bold" }}>
                  {analysisCommentsKeyMapping[step]}
                </Text>
              </PDFTableCell>
            ))}
          </PDFTableRow>

          <PDFTableBody>
            {comments?.map((row, _) => {
              const recommendationData =
                productRecommendations?.[row.productId];

              const containsMultipleLinesBadge =
                checkContainsMultipleLinesRecommendationBadge({
                  selectedProducts: [
                    {
                      recommendation: recommendationData,
                    },
                  ],
                  productRecommendations,
                });

              return (
                <PDFTableRow key={row.productId} color={"white"}>
                  <PDFTableCell
                    width={sizes?.firstCellWidth}
                    alignItems="center"
                    justifyContent={
                      recommendationData ? "space-between" : "center"
                    }
                    paddingHorizontal={0}
                    paddingVertical={0}
                  >
                    <View
                      style={{
                        flexDirection: "column",
                        gap: "10px",
                        alignItems: "center",
                        margin: "auto 0",
                        paddingTop: 5,
                        paddingBottom: !recommendationData ? 5 : 0,
                      }}
                    >
                      {row?.productAvatar ? (
                        <Image
                          src={
                            USE_PDF_LOCAL_ASSETS
                              ? PDFDefaultPlatformLogoS3
                              : generatePDFImageIconUrl(
                                  parseCommentProductLogo(row?.productAvatar),
                                )
                          }
                          style={{ width: 20, height: 20 }}
                        />
                      ) : (
                        <Image
                          src={PDFDefaultPlatformLogoS3}
                          style={{ width: 20, height: 20 }}
                        />
                      )}

                      <Text
                        style={{
                          ...typographyStyle?.body2,
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        {row?.productName}
                      </Text>
                    </View>

                    <RecommendationLabel
                      initialProductRecommendation={recommendationData}
                      shouldIncreaseHeight={containsMultipleLinesBadge}
                    />
                  </PDFTableCell>
                  {isAppendix && (
                    <PDFTableCell width={sizes?.secondCellWidth}>
                      <Text
                        style={{
                          ...typographyStyle?.body2,
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        {`${row?.featuresMatch} %`}
                      </Text>
                    </PDFTableCell>
                  )}
                  {steps?.map((step) => {
                    const removedAtStep = row?.removedFromShortlistAtStep;
                    const isShowRemovedLabel =
                      isAppendix && removedAtStep === step;
                    const isShowDashCharacter =
                      step &&
                      removedAtStep &&
                      steps.indexOf(step) > steps.indexOf(removedAtStep) &&
                      step !== AnalysisStep.Summary &&
                      isAppendix;

                    const comment =
                      row[
                        analysisCommentsKeyMapping[
                          step
                        ] as keyof PDFReviewComment
                      ];

                    return (
                      <PDFTableCell
                        key={step}
                        justifyContent={comment ? "flex-start" : "center"}
                        width={sizes?.cellWidth}
                      >
                        <View
                          style={{
                            alignItems: "center",
                            gap: "2px",
                          }}
                        >
                          <Text
                            style={{
                              ...typographyStyle?.body2,
                              maxHeight: "auto",
                              fontSize: 7,
                            }}
                          >
                            {comment ? (
                              <PDFRichText
                                htmlString={comment.toString()}
                                fontSize={7}
                              />
                            ) : row?.isContinued ? (
                              ""
                            ) : isShowDashCharacter ? (
                              "-"
                            ) : (
                              "No comment"
                            )}
                          </Text>
                          {isShowRemovedLabel && (
                            <View
                              style={{
                                height: "15px",
                                border: "1px solid #FFAC8A",
                                backgroundColor: "#FFE3D8",
                                borderRadius: "8px",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "90px",
                              }}
                            >
                              <Text
                                style={{
                                  ...typographyStyle?.body2,
                                  color: "#f01d0a",
                                  fontSize: "7px",
                                }}
                              >
                                Removed from shortlist
                              </Text>
                            </View>
                          )}
                        </View>
                      </PDFTableCell>
                    );
                  })}
                </PDFTableRow>
              );
            })}
          </PDFTableBody>
        </PDFTable>
      </View>
    </View>
  );
};
